/* eslint-disable sort-exports/sort-exports */
import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../../index";
import { updateAuthStateAtom } from "../shared/atoms";

export const GetAppAuthParams = z.object({
  implementation_id: z
    .string()
    .describe(
      "The app implementation ID with version (e.g., 'GoogleSheetsV2CLIAPI@2.8.0') from get_app_needs",
    ),
  action_key: z
    .string()
    .describe(
      "The specific action key that requires authentication (NOT step_id - use the action key from get_app_needs)",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Checking authentication...' or 'Verifying access...'",
    ),
});

export type GetAppAuthParamsType = z.infer<typeof GetAppAuthParams>;

export const getAppAuthExecute = async (rawArgs: any): Promise<any> => {
  let params: GetAppAuthParamsType;
  try {
    params = GetAppAuthParams.parse(rawArgs || {});
  } catch (error) {
    console.error("[GET_APP_AUTH] Error parsing arguments:", error);
    return {
      $error: {
        message: `Error: Invalid arguments provided for get_app_auth. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
      },
    };
  }

  const { implementation_id, action_key } = params;

  try {
    // Validate implementation_id format
    if (!/^[A-Za-z0-9]+@\d+\.\d+\.\d+$/.test(implementation_id)) {
      return {
        $error: {
          message: `Invalid implementation_id format: ${implementation_id}. Expected format: AppName@version (e.g., GmailCLIAPI@1.5.0)`,
        },
      };
    }

    // Call our backend route to get real authentication data
    const apiUrl = `/api/canvasui/composer/getAppAuth?implementation_id=${encodeURIComponent(implementation_id)}`;

    // eslint-disable-next-line no-console
    console.log(`[GET_APP_AUTH] Checking auths for ${implementation_id}`);

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include", // Include cookies for authentication
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = {
          error: `API returned ${response.status}: ${response.statusText}`,
        };
      }

      // Handle apps that don't support authentication
      if (errorData.authentication_not_supported) {
        // eslint-disable-next-line no-console
        console.log(
          `[GET_APP_AUTH] ${implementation_id} does not support authentication`,
        );
        return {
          content: [
            {
              type: "text",
              text: JSON.stringify(
                {
                  implementation_id,
                  action_key,
                  app_name: errorData.app_name,
                  authentication_status: "not_supported",
                  authentication_not_supported: true,
                  message: `✅ ${errorData.app_name} does not require authentication. You can proceed with field mapping without authentication setup.`,
                  next_steps: [
                    "Skip authentication - not required for this app",
                    "Proceed directly to get_app_choices for field options",
                    "Configure field mappings without auth concerns",
                  ],
                  skip_auth: true,
                },
                null,
                2,
              ),
            },
          ],
        };
      }

      console.error(`[GET_APP_AUTH] Error fetching auth data:`, errorData);
      return {
        $error: {
          message: `Error checking authentications: ${errorData.error || "Unknown error"}`,
        },
      };
    }

    const authData = await response.json();
    const {
      authentications,
      suggested_auth_id,
      has_valid_auth,
      needs_new_auth,
      app_name,
    } = authData;

    // Build response based on authentication status
    let responseText;
    let status: "success" | "needs_selection" | "needs_auth" | "error";

    if (needs_new_auth) {
      // No authentications exist - need to trigger auth setup
      status = "needs_auth";
      responseText = {
        implementation_id,
        action_key,
        app_name,
        authentication_status: "missing",
        STOP_FIELD_MAPPING: true,
        requires_immediate_action: "create_auth",
        message: `BLOCKED: No authentication found for ${app_name}. Field mapping cannot proceed until authentication is set up.`,
        authentications: [],
        suggested_action: "create_auth",
        next_steps: [
          "IMMEDIATE: Use create_auth tool to set up new authentication",
          "Do NOT attempt field mapping or get_app_choices until auth is created",
          "Once authentication is created, retry get_app_auth to verify",
          "Only then proceed to get_app_choices for field options",
        ],
      };
    } else if (authentications.length === 1) {
      // Single auth exists - MUST get explicit user confirmation
      const auth = authentications[0];
      status = "needs_selection";
      responseText = {
        implementation_id,
        action_key,
        app_name,
        authentication_status: has_valid_auth
          ? "single_valid"
          : "single_needs_refresh",
        REQUIRES_USER_INPUT: true,
        BLOCKS_FIELD_MAPPING: true,
        message: `🔴 AUTHENTICATION CONFIRMATION REQUIRED: Found 1 authentication for ${app_name}. You must confirm to proceed.`,
        authentications: [auth],
        suggested_auth_id: auth.id,
        suggested_action: "confirm_auth",
        critical_warning:
          "⚠️ AI MUST PAUSE: Do not proceed to get_app_choices until user explicitly confirms this authentication.",
        confirmation_required: `Please confirm you want to use: "${auth.label}" (Status: ${auth.status}, ID: ${auth.id}) for field mapping?`,
        valid_responses: ["yes", "use it", "confirmed", "no", "create new"],
        next_steps: [
          "🚫 BLOCKED: Wait for explicit user confirmation",
          "Valid responses: 'yes', 'use it', 'confirmed', 'no', 'create new'",
          "Only proceed to get_app_choices AFTER user confirms",
          "If user says no: offer to create new authentication",
        ],
      };
    } else {
      // Multiple auths exist - MUST get explicit user selection
      status = "needs_selection";
      const suggestedAuth = authentications.find(
        (a: any) => a.id === suggested_auth_id,
      );
      responseText = {
        implementation_id,
        action_key,
        app_name,
        authentication_status: "multiple_available",
        REQUIRES_USER_INPUT: true,
        BLOCKS_FIELD_MAPPING: true,
        message: `🔴 AUTHENTICATION SELECTION REQUIRED: Found ${authentications.length} authentications for ${app_name}. You must select one to proceed.`,
        authentications: authentications.slice(0, 5), // Limit display to 5 for readability
        suggested_auth_id,
        suggested_action: "select_auth",
        critical_warning:
          "⚠️ AI MUST PAUSE: Do not proceed to get_app_choices until user explicitly selects an authentication.",
        suggested_message: suggestedAuth
          ? `I suggest using: "${suggestedAuth.label}" (ID: ${suggested_auth_id}, most recently used)`
          : null,
        selection_required:
          "Please tell me which auth_id you want to use, or say 'use suggested', or 'create new'",
        valid_responses: ["auth_id", "use suggested", "create new"],
        next_steps: [
          "🚫 BLOCKED: Wait for explicit user selection",
          "Review the available authentications listed above",
          `Suggested: "${suggestedAuth?.label}" (ID: ${suggested_auth_id})`,
          "Valid responses: specific auth_id, 'use suggested', or 'create new'",
          "Only proceed to get_app_choices AFTER user selects",
        ],
      };
    }

    // eslint-disable-next-line no-console
    console.log(
      `[GET_APP_AUTH] Status: ${status}, Found ${authentications.length} auths, Valid: ${has_valid_auth}`,
    );

    return {
      content: [
        {
          type: "text",
          text: JSON.stringify(responseText, null, 2),
        },
      ],
    };
  } catch (error) {
    console.error("[GET_APP_AUTH] Error in execution:", error);
    return {
      $error: {
        message: `Error checking app authentication: ${error instanceof Error ? error.message : String(error)}`,
      },
    };
  }
};

export const getAppAuthDescription = `Handles authentication verification and management for a specific Zapier app action.

This tool checks for existing authentications, verifies their status, and guides users 
through the authentication selection or creation process.

**MANDATORY USAGE**: Always call this tool after get_app_needs and before get_app_choices.

Parameters:
- implementation_id: Full app identifier with version from get_app_needs (e.g., "GmailCLIAPI@1.5.0")
- action_key: The specific action key from get_app_needs response (e.g., "new_event", "send_email") - NOT step_id

Returns different responses based on authentication status:

**No Authentication (needs_new_auth: true)**:
- BLOCKS all field mapping operations immediately
- Sets STOP_FIELD_MAPPING: true and requires_immediate_action: "create_auth"
- Provides clear error: "BLOCKED: Cannot proceed until authentication is set up"
- Must complete auth setup before proceeding to field mapping

**Single Authentication Found**:
- Shows the available authentication with status (valid/expired)
- Asks user to confirm they want to use this connection
- Provides auth_id for use in subsequent get_app_choices calls

**Multiple Authentications Found**:
- Lists all available authentications with their status
- Suggests the most recently used valid authentication  
- Requires user selection before proceeding
- Limits display to 5 auths for readability

**Critical Rule**: Never proceed to get_app_choices without confirmed authentication.
The user must either select an existing auth or create a new one.

Use this tool in the V2 hierarchical sequence: get_app_needs → **get_app_auth** → get_app_choices`;

export const getAppAuthAtom = atom<JotaiTool<z.infer<typeof GetAppAuthParams>>>(
  () => ({
    name: "get_app_auth",
    description: getAppAuthDescription,
    parameters: GetAppAuthParams,
    execute: atom(
      null, // read function (not used for write-only atom)
      async (get, set, { args }) => {
        const result = await getAppAuthExecute(args);

        // Update auth state based on the result
        if (result.content?.[0]?.text) {
          try {
            const authData = JSON.parse(result.content[0].text);
            const {
              implementation_id,
              authentications,
              authentication_status,
            } = authData;

            // ENHANCED: Update the auth state atom with enforcement flags
            set(updateAuthStateAtom, {
              implementation_id,
              status:
                authentication_status === "missing" ? "missing" : "unknown",
              needs_selection: authentications && authentications.length > 1,
              available_auths:
                authentications?.map((auth: any) => ({
                  id: auth.id,
                  label: auth.label,
                  status: auth.status,
                })) || [],
              confirmed: false, // Always requires user confirmation
              // ENFORCEMENT FLAGS
              requires_user_confirmation:
                authentications && authentications.length > 0,
              blocks_field_mapping: true, // Block until user confirms
              user_confirmation_timestamp: undefined, // Clear any previous confirmation
            });
          } catch (error) {
            console.error("[GET_APP_AUTH] Error updating auth state:", error);
          }
        }

        return result;
      },
    ),
  }),
);

getAppAuthAtom.debugLabel = "getAppAuthAtom";

/* eslint-enable sort-exports/sort-exports */
